import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Stack, styled, Typography } from '@mui/material'
import type { ButtonProps } from '@mui/material'
import type { FC, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import type { HoldingWeekDto } from '@hcr/api/consumer'
import { COLORS, ICONS, LinkUnstyled, unit } from '@hcr/ui'

import { DateFormat, Path } from '../../../../../../../models'
import { dateFormatter, to } from '../../../../../../../utils'

interface WeekPreviewProps {
  actionRef?: (element: HTMLAnchorElement | null) => void
  holdingId: string
  week: HoldingWeekDto
}

export const WeekPreview: FC<WeekPreviewProps> = ({ holdingId, week, actionRef }) => {
  const { t } = useTranslation()

  const isReserved = week.is_reservation_request_received
  const isCancellable = isReserved && !week.is_locked

  const variant = week.is_locked ? 'text' : isReserved ? 'contained' : 'outlined'

  const actionLabel =
    week.is_locked || week.is_reservation_request_received ? t('owner-holding.booked') : t('owner-holding.select-week')

  const weekLabel = t('owner-holding.week', { weekNumber: week.week_number })
  const dateRangeLabel = dateFormatter.formatDestinationDateRange({
    from: week.week_start,
    fromFormat: DateFormat.Basic,
    to: week.week_end,
    toFormat: DateFormat.Basic,
  })

  const actionAriaLabel = [
    `${weekLabel}, ${dateRangeLabel}`,
    actionLabel,
    isCancellable && `${t('owner-holding.own-reservation')}, ${t('owner-holding.edit')}`,
    week.is_locked && t('owner-holding.not-available'),
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <Stack
      direction='row'
      justifyContent='space-between'
      alignItems='center'
      flexWrap='wrap'
      rowGap={unit(1)}
      paddingTop={unit(3.5)}
      paddingRight={unit(3)}
      paddingBottom={unit(2.5)}
      paddingLeft={unit(3.75)}
    >
      <WeekLabels>
        <Typography variant='labelM'>{weekLabel}</Typography>
        <Typography variant='bodyM' color={COLORS.grey[700]}>
          {dateRangeLabel}
        </Typography>
      </WeekLabels>
      <ActionWrapper>
        <ActionButton
          ref={actionRef}
          $isCancellable={isCancellable}
          component={LinkUnstyled}
          to={to([Path.OwnerHoldingBooking, { holdingId, weekStart: week.week_start }])}
          onClick={week.is_locked ? event => event.preventDefault() : undefined}
          aria-label={actionAriaLabel}
          startIcon={
            isCancellable && <FontAwesomeIcon icon={ICONS.farPencil} style={{ fontSize: unit(4) }} aria-hidden='true' />
          }
          endIcon={
            week.is_locked && (
              <FontAwesomeIcon icon={ICONS.farLockKeyhole} style={{ fontSize: unit(4) }} aria-hidden='true' />
            )
          }
          disabled={week.is_locked}
          variant={variant}
          color='primary'
          style={{ textAlign: 'right' }}
        >
          {actionLabel}
        </ActionButton>
      </ActionWrapper>
    </Stack>
  )
}

const WeekLabels = styled(Stack)`
  min-width: 0;
  overflow-wrap: break-word;
`

const ActionWrapper = styled(Stack)`
  padding: ${unit(1.25)};
  margin-left: auto;
  min-width: 0;
  max-width: 100%;
`

const ActionButton = styled(Button, { shouldForwardProp: prop => prop !== '$isCancellable' })<{
  $isCancellable: boolean
}>`
  border-radius: ${props => (props.variant === 'outlined' ? 0 : unit(1))};
  padding: ${unit(1)} ${unit(2.5)};
  font-size: ${unit(3)};
  white-space: normal;
  overflow-wrap: break-word;

  & .MuiButton-startIcon {
    margin-right: ${unit(1.5)};
  }

  & .MuiButton-endIcon {
    margin-left: ${unit(1.5)};
  }

  color: ${props =>
    props.$isCancellable
      ? COLORS.burgundi[500]
      : props.variant === 'outlined'
        ? COLORS.black
        : props.variant === 'text'
          ? COLORS.grey[700]
          : undefined};
  border-color: ${props => (props.variant === 'outlined' ? COLORS.black : undefined)};
` as (props: ButtonProps<typeof LinkUnstyled, { $isCancellable: boolean }>) => ReactElement
